import {
  type EditAreaAdminState,
  editAreaAdminStateSchema,
  FACE_KEYS,
  type FaceDefinition,
  type FaceKey,
  isRequiredFace,
  type ProductMeasurementKind,
} from '@repo/api-contracts';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { compositeOnly } from '../lib/composite-asset';
import { draftAssetKeysOf } from '../lib/draft-asset-keys';
import { unsupportedReasonsOf } from '../lib/unsupported-definition';
import { createEditAreaCompositeCommands } from './edit-area-composite-commands';
import { editAreaDraftSnapshot } from './edit-area-draft-snapshot';
import type {
  EditAreaDraftOptions,
  EditAreaDraftState,
  EditAreaSelection,
} from './edit-area-draft-types';
import { createEditAreaGeometryCommands } from './edit-area-geometry-commands';
import { createPhotoDraftSlice } from './photo-draft-slice';
import type { PhotoDraftState } from './photo-draft-types';

/** 응답 면 정의에서 합성 표시 URL 을 뺀 저장 모양. URL 은 사진 slice 가 photoUrls 맵에 넣는다. */
const faceOnly = (face: FaceDefinition): FaceDefinition =>
  face.composite === undefined ? face : { ...face, composite: compositeOnly(face.composite) };

const geometryOf = (
  server: EditAreaAdminState | null,
  measurementKind: ProductMeasurementKind,
): Pick<EditAreaDraftState, 'components' | 'faces' | 'unsupportedReasons' | 'isUnsupported'> => {
  const definition = server?.viewing.definition;
  const faces: EditAreaDraftState['faces'] = definition
    ? {
        front: faceOnly(definition.faces.front),
        ...(definition.faces.back === undefined ? {} : { back: faceOnly(definition.faces.back) }),
        ...(definition.faces.left === undefined ? {} : { left: faceOnly(definition.faces.left) }),
        ...(definition.faces.right === undefined
          ? {}
          : { right: faceOnly(definition.faces.right) }),
      }
    : { front: { surfaces: [], placements: [] }, back: { surfaces: [], placements: [] } };
  const unsupportedReasons = unsupportedReasonsOf(faces, measurementKind);
  return {
    components: definition?.components ?? [],
    faces,
    unsupportedReasons,
    isUnsupported: unsupportedReasons.length > 0,
  };
};

const selectionOf = (
  state: Pick<EditAreaDraftState, 'selection' | 'draft' | 'faces' | 'components'>,
): EditAreaSelection => {
  const current = state.selection;
  const face = state.draft.activeFaces.includes(current.face) ? current.face : 'front';
  const component = state.components.find((value) => value.id === current.componentId);
  const holes = component?.shape?.type === 'polygon' ? (component.shape.holes ?? []) : [];
  return {
    face,
    colorOptionId: state.draft.colors.some((color) => color.colorOptionId === current.colorOptionId)
      ? current.colorOptionId
      : (state.draft.colors[0]?.colorOptionId ?? null),
    componentId: state.components.some((component) => component.id === current.componentId)
      ? current.componentId
      : null,
    // 면당 표면은 1개라 선택이 없거나 무효면 그 면의 첫 표면으로 정한다.
    surfaceId: state.faces[face]?.surfaces.some((surface) => surface.id === current.surfaceId)
      ? current.surfaceId
      : (state.faces[face]?.surfaces[0]?.id ?? null),
    // 고른 영역에 그 순번의 구멍이 있어야 한다(영역이 없어지거나 구멍이 지워지면 풀린다).
    holeIndex:
      current.holeIndex !== null && holes[current.holeIndex] !== undefined
        ? current.holeIndex
        : null,
  };
};

/** 저장 원본은 이 store 하나다. 사진 slice와 기하를 원자적으로 갱신한다. */
export const createEditAreaDraftStore = (
  options: EditAreaDraftOptions,
): StoreApi<EditAreaDraftState> =>
  createStore<EditAreaDraftState>((set, get) => {
    const initialServer = options.serverState
      ? editAreaAdminStateSchema.parse(options.serverState)
      : null;
    let baseline = '';
    const commit = (patch: Partial<EditAreaDraftState>): void => {
      const next = { ...get(), ...patch };
      set({
        ...patch,
        commandError: null,
        selection: selectionOf(next),
        dirty:
          Object.keys(next.pendingInputs).length > 0 || editAreaDraftSnapshot(next) !== baseline,
      });
    };
    const reject = (message: string): false => {
      set({ commandError: message });
      return false;
    };
    const { measurementKind } = options;
    const canEdit = (): boolean =>
      (!get().locked && !get().isUnsupported) ||
      reject('현재 저장 요청 중이거나 편집할 수 없는 정의입니다.');
    const setPhotos = (patch: Partial<PhotoDraftState>): void => {
      const state = get();
      const draft = patch.draft ?? state.draft;
      const faces = { ...state.faces };
      for (const face of FACE_KEYS) {
        if (draft.activeFaces.includes(face)) faces[face] ??= { surfaces: [], placements: [] };
        else if (!isRequiredFace(face)) delete faces[face];
      }
      const facesChanged = FACE_KEYS.some((face) => state.faces[face] !== faces[face]);
      const removedFaces = state.draft.activeFaces.filter(
        (face) => !draft.activeFaces.includes(face),
      );
      const pendingInputs =
        removedFaces.length > 0
          ? Object.fromEntries(
              Object.entries(state.pendingInputs).filter(
                ([key]) =>
                  !removedFaces.some((face) => key.startsWith(`placement:${face}:`)) &&
                  !(key === 'surface' && removedFaces.includes(state.selection.face)),
              ),
            )
          : state.pendingInputs;
      commit({ ...patch, faces: facesChanged ? faces : state.faces, pendingInputs });
    };
    const photos = createPhotoDraftSlice(
      {
        ...options,
        definition: initialServer?.viewing.definition,
        autoHydrate: false,
        allowSavedFaceRemoval: true,
        canEdit: () => !get().isUnsupported,
      },
      setPhotos,
      get,
    );
    const geometry = geometryOf(initialServer, measurementKind);
    baseline = editAreaDraftSnapshot({ ...photos, ...geometry });
    return {
      ...photos,
      ...geometry,
      serverState: initialServer,
      measurement: options.measurement,
      measurementKind,
      pendingInputs: {},
      selection: selectionOf({
        ...photos,
        ...geometry,
        selection: {
          colorOptionId: options.colorOptionIds[0] ?? null,
          face: 'front',
          componentId: null,
          surfaceId: null,
          holeIndex: null,
        },
      }),
      commandError: null,
      shapeDrawing: null,
      ...createEditAreaGeometryCommands({ get, set, commit, reject, canEdit }),
      ...createEditAreaCompositeCommands({ get, commit, reject, canEdit }),
      mergePhotoUrls: (photos) => {
        const state = get();
        const currentKeys = new Set(draftAssetKeysOf(state));
        const fresh = Object.fromEntries(
          photos
            .filter((photo) => currentKeys.has(photo.photoKey))
            .map((photo) => [photo.photoKey, photo.photoUrl]),
        );
        set({ photoUrls: { ...state.photoUrls, ...fresh } });
      },
      setLocked: (locked) => set({ locked }),
      setMeasurement: (measurement) => {
        const current = get().measurement;
        const unchanged =
          current === measurement ||
          (current !== null &&
            measurement !== null &&
            current.widthMm === measurement.widthMm &&
            current.heightMm === measurement.heightMm);
        if (!unchanged) set({ measurement });
      },
      setInputDirty: (key, dirty) => {
        if (dirty && !canEdit()) return;
        commit({
          pendingInputs: dirty
            ? { ...get().pendingInputs, [key]: true }
            : Object.fromEntries(
                Object.entries(get().pendingInputs).filter(([entry]) => entry !== key),
              ),
        });
      },
      setSelection: (selection) => {
        if (get().locked) return;
        const current = get().selection;
        // 다른 영역·면을 고르면 구멍 선택은 풀린다(같이 넘긴 holeIndex 가 있으면 그것을 쓴다).
        const targetChanged =
          (selection.componentId !== undefined && selection.componentId !== current.componentId) ||
          (selection.face !== undefined && selection.face !== current.face);
        const next = selectionOf({
          ...get(),
          selection: {
            ...current,
            ...(targetChanged ? { holeIndex: null } : {}),
            ...selection,
          },
        });
        set({
          selection: next,
          ...(next.face === get().selection.face ? {} : { shapeDrawing: null }),
        });
      },
      applyTemplateGeometry: (geometry) => {
        const state = get();
        if (state.locked) return reject('현재 저장 요청 중입니다. 끝난 뒤 다시 적용해 주세요.');
        if (Object.keys(state.operations).length > 0)
          return reject('진행 중이거나 실패한 업로드가 있습니다. 정리한 뒤 다시 적용해 주세요.');
        const activeFaces = FACE_KEYS.filter(
          (face) => isRequiredFace(face) || geometry.faces[face] !== undefined,
        );
        const faceOf = (face: FaceKey): FaceDefinition | undefined => {
          const template = geometry.faces[face];
          if (template === undefined) return undefined;
          const composite = state.faces[face]?.composite;
          const keepComposite =
            composite !== undefined &&
            template.surfaces.some((surface) => surface.type === 'cylinder');
          return keepComposite ? { ...template, composite } : { ...template };
        };
        const front = faceOf('front');
        if (front === undefined) return false;
        const back = faceOf('back');
        const left = faceOf('left');
        const right = faceOf('right');
        const faces: EditAreaDraftState['faces'] = {
          front,
          ...(back === undefined ? {} : { back }),
          ...(left === undefined ? {} : { left }),
          ...(right === undefined ? {} : { right }),
        };
        const draft = {
          ...state.draft,
          activeFaces,
          colors: state.draft.colors.map((color) => ({
            colorOptionId: color.colorOptionId,
            faces: Object.fromEntries(
              activeFaces.flatMap((face) => {
                const photo = color.faces[face];
                return photo === undefined ? [] : [[face, photo]];
              }),
            ),
          })),
        };
        const unsupportedReasons = unsupportedReasonsOf(faces, measurementKind);
        commit({
          components: [...geometry.components],
          faces,
          draft,
          unsupportedReasons,
          isUnsupported: unsupportedReasons.length > 0,
          lockedFaces: [],
          pendingInputs: {},
          generation: state.generation + 1,
          notice: null,
          shapeDrawing: null,
        });
        options.onPhotosChange?.(draft);
        return true;
      },
      applyServerState: (response, ids) => {
        const state = get();
        const serverState = response ? editAreaAdminStateSchema.parse(response) : null;
        if (
          serverState &&
          serverState.viewing.productId.toLowerCase() !== state.draft.productId.toLowerCase()
        ) {
          reject('다른 상품의 편집 영역 응답은 적용할 수 없습니다.');
          return;
        }
        const nextPhoto = createPhotoDraftSlice(
          {
            ...options,
            colorOptionIds: ids ?? state.draft.colors.map((color) => color.colorOptionId),
            definition: serverState?.viewing.definition,
            autoHydrate: false,
            allowSavedFaceRemoval: true,
          },
          () => {},
          get,
        );
        const next = {
          ...state,
          ...geometryOf(serverState, measurementKind),
          draft: nextPhoto.draft,
          photoUrls: nextPhoto.photoUrls,
          lockedFaces: [],
          operations: {},
          pendingInputs: {},
          serverState,
          generation: state.generation + 1,
          locked: false,
          dirty: false,
          notice: null,
          commandError: null,
          shapeDrawing: null,
        };
        baseline = editAreaDraftSnapshot(next);
        set({ ...next, selection: selectionOf(next) });
        options.onPhotosChange?.(next.draft);
      },
    };
  });
